import {
  useBrokersServiceBrokersBrokersMediaCreate,
  useBrokersServiceBrokersBrokersMediaListKey
} from '@insurance/api/queries'
import { useValidationsField } from '@insurance/hooks'
import { Text } from '@shared/components'
import { showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { App, Flex, Form, Input, Modal } from 'antd'
import Dragger from 'antd/es/upload/Dragger'
import { DocumentUpload } from 'iconsax-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { CommonPropsMedia } from './types'

type Props = Omit<CommonPropsMedia, 'data'>

export const UploadMedia = ({ broker, onSuccess, ...props }: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { required } = useValidationsField()

  const fileWatch = Form.useWatch('file', form)

  useEffect(() => {
    const fileName = fileWatch?.fileList?.[0]?.name
    if (fileName && !form.getFieldValue('file_name')) {
      form.setFieldValue('file_name', fileName.replace(/\.[^/.]+$/, ''))
    }
  }, [fileWatch, form])

  const { mutate, isPending } = useBrokersServiceBrokersBrokersMediaCreate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useBrokersServiceBrokersBrokersMediaListKey]
      })
      message.success(t('added_successfully'))
      onSuccess()
    },
    onError: (error) => showApiErrors(error, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    const file = values.file?.fileList?.[0]?.originFileObj

    const originalFileName = values.file?.fileList?.[0]?.name
    const extension = originalFileName?.split('.').pop()

    mutate({
      formData: {
        id: 0,
        broker,
        file,
        name: values.file_name + (extension ? `.${extension}` : ''),
        notes: values.notes
      }
    })
  }

  return (
    <Modal
      title={t('upload_file')}
      onOk={onSubmit}
      okButtonProps={{ loading: isPending }}
      afterClose={() => form.resetFields()}
      {...props}
    >
      <Form layout="vertical" form={form}>
        <Form.Item name="file" label={t('file')} rules={[required]}>
          <Dragger
            maxCount={1}
            customRequest={({ onSuccess }) => onSuccess?.({})}
          >
            <Flex vertical gap={8} align="center" style={{ padding: '8px 0' }}>
              <DocumentUpload size={24} />
              <Text>{t('upload_file_text')}</Text>
            </Flex>
          </Dragger>
        </Form.Item>

        <Form.Item name="file_name" label={t('file_name')} rules={[required]}>
          <Input placeholder={t('file_name')} />
        </Form.Item>

        <Form.Item name="notes" label={t('notes')}>
          <Input.TextArea rows={3} placeholder={t('notes')} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
